<script setup lang="ts">
import type { ContentTocItem } from '~/utils/contentToc'

type TocNode = ContentTocItem & {
  parentId: string | null
  hasChildren: boolean
}

const props = withDefaults(
  defineProps<{
    items: ContentTocItem[]
    activeId?: string
    collapsed?: boolean
    collapsible?: boolean
    variant?: 'inline' | 'aside'
  }>(),
  {
    activeId: '',
    collapsed: false,
    collapsible: false,
    variant: 'inline'
  }
)

const emit = defineEmits<{
  (event: 'toggle'): void
}>()

const collapsedGroups = ref<Set<string>>(new Set())

const treeNodes = computed(() => {
  const nodes: TocNode[] = []
  const ancestry: TocNode[] = []

  props.items.forEach(item => {
    if (!item?.id) {
      return
    }
    const level = Number(item.level || 1)
    const node: TocNode = {
      id: String(item.id),
      text: String(item.text || '').trim() || '未命名标题',
      level,
      parentId: null,
      hasChildren: false
    }

    while (ancestry.length > 0) {
      const parent = ancestry[ancestry.length - 1]
      if (parent && parent.level < level) {
        break
      }
      ancestry.pop()
    }

    const parent = ancestry[ancestry.length - 1]
    if (parent) {
      node.parentId = parent.id
      parent.hasChildren = true
    }

    ancestry.push(node)
    nodes.push(node)
  })

  return nodes
})

const visibleItems = computed(() => {
  const nodeById = new Map(treeNodes.value.map(item => [item.id, item]))
  return treeNodes.value.filter(item => {
    let parentId = item.parentId
    while (parentId) {
      if (collapsedGroups.value.has(parentId)) {
        return false
      }
      parentId = nodeById.get(parentId)?.parentId || null
    }
    return true
  })
})

const isCollapsed = (id: string) => collapsedGroups.value.has(id)
const toggleCollapse = (id: string) => {
  const next = new Set(collapsedGroups.value)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  collapsedGroups.value = next
}

watch(treeNodes, nodes => {
  const validIds = new Set(nodes.map(item => item.id))
  collapsedGroups.value = new Set([...collapsedGroups.value].filter(id => validIds.has(id)))
})
</script>

<template>
  <section :class="['post-toc', `post-toc-${variant}`, { 'is-collapsed': collapsible && collapsed }]">
    <div class="post-toc-head">
      <strong><i class="fa-solid fa-list" aria-hidden="true" />目录</strong>
      <button v-if="collapsible" type="button" class="post-toc-toggle" @click="emit('toggle')">
        <i :class="collapsed ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-up'" aria-hidden="true" />
        {{ collapsed ? '展开' : '隐藏' }}
      </button>
    </div>
    <div class="post-toc-body" :class="{ collapsed: collapsible && collapsed }">
      <div class="post-toc-list-wrap">
        <ul class="post-toc-list">
          <li
            v-for="item in visibleItems"
            :key="item.id"
            class="post-toc-item"
            :class="{ 'is-active': item.id === activeId }"
            :style="{ paddingLeft: `${Math.max(0, item.level - 1) * 8}px` }"
          >
            <div class="post-toc-item-content">
              <button
                v-if="item.hasChildren"
                type="button"
                class="post-toc-collapse-btn"
                :aria-expanded="!isCollapsed(item.id)"
                title="展开/收起子目录"
                @click="toggleCollapse(item.id)"
              >
                <i class="fa-solid fa-chevron-down post-toc-collapse-icon" :class="{ 'is-collapsed': isCollapsed(item.id) }" aria-hidden="true" />
              </button>
              <span v-else class="post-toc-collapse-placeholder" aria-hidden="true" />
              <a class="post-toc-link" :href="`#${encodeURIComponent(item.id)}`">
                {{ item.text }}
              </a>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<style scoped>
.post-toc {
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--bg-soft);
  overflow: hidden;
}

.post-toc-aside {
  background: color-mix(in srgb, var(--bg-soft) 92%, transparent);
  box-shadow: var(--shadow);
  backdrop-filter: blur(14px);
}

.post-toc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 13px 14px 10px;
  border-bottom: 1px solid color-mix(in srgb, var(--line) 70%, transparent);
  background: color-mix(in srgb, var(--bg) 42%, transparent);
  transition: border-color 0.2s ease;
}

.post-toc.is-collapsed .post-toc-head {
  border-bottom-color: transparent;
}

.post-toc-head strong {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--text);
  letter-spacing: 0.2px;
}

.post-toc-head strong i {
  opacity: 0.82;
}

.post-toc-toggle {
  border: 0;
  background: transparent;
  color: var(--text-soft);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 0;
}

.post-toc-body {
  display: grid;
  grid-template-rows: 1fr;
  opacity: 1;
  transition:
    grid-template-rows 0.24s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.2s ease;
}

.post-toc-body.collapsed {
  grid-template-rows: 0fr;
  opacity: 0;
}

.post-toc-list-wrap {
  min-height: 0;
  overflow: hidden;
}

.post-toc-list {
  margin: 0;
  padding: 8px 6px 10px;
  list-style: none;
  display: grid;
  gap: 2px;
  overflow: hidden;
}

.post-toc-aside .post-toc-list-wrap {
  max-height: calc(100vh - var(--anchor-offset) - 100px);
  overflow-y: auto;
  scrollbar-width: thin;
}

.post-toc-item {
  position: relative;
  border-radius: 8px;
  transition: background-color 0.18s ease;
}

.post-toc-item::before {
  content: '';
  position: absolute;
  left: 6px;
  top: 7px;
  bottom: 7px;
  width: 3px;
  border-radius: 999px;
  background: var(--brand);
  opacity: 0;
  transform: scaleY(0.3);
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.post-toc-item.is-active::before {
  opacity: 0.85;
  transform: scaleY(1);
}

.post-toc-item.is-active {
  background: color-mix(in srgb, var(--brand-soft) 68%, transparent);
}

.post-toc-item-content {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  padding: 5px 4px;
}

.post-toc-collapse-btn,
.post-toc-collapse-placeholder {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
}

.post-toc-collapse-btn {
  border: 0;
  border-radius: 5px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-soft);
  background: transparent;
  cursor: pointer;
}

.post-toc-collapse-icon {
  font-size: 11px;
  transition: transform 0.22s cubic-bezier(0.25, 1, 0.5, 1);
}

.post-toc-collapse-icon.is-collapsed {
  transform: rotate(-90deg);
}

.post-toc-link {
  flex: 1;
  min-width: 0;
  color: var(--text-soft);
  display: block;
  padding: 0;
  font-size: 13px;
  line-height: 1.5;
  text-decoration: none;
  word-break: break-word;
  transition: color 0.15s ease;
}

.post-toc-item.is-active .post-toc-link {
  color: var(--brand);
  font-weight: 600;
}
</style>
